<template>
    <div
        class="todo-step-card"
        :class="{
            'todo-step-card--acknowledged': step.acknowledged,
            'todo-step-card--focused': isFocused,
            'todo-step-card--closed': step.modification_closed,
        }"
        :data-step-key="step.key"
    >
        <div class="todo-step-card__header">
            <span class="todo-step-card__status">
                <svg v-if="step.acknowledged" viewBox="0 0 72.241 51.652" class="todo-step-card__icon"><use xlink:href="#check"></use></svg>
                <svg v-else viewBox="0 0 24 24" class="todo-step-card__icon"><use :xlink:href="'#' + step.icon"></use></svg>
            </span>
            <h3 class="todo-step-card__label">{{ step.label }}</h3>
        </div>

        <div v-if="step.profile_preview" class="todo-step-card__profile">
            <div class="todo-step-card__profile-logo">
                <img
                    v-if="step.profile_preview.logo_url"
                    :src="step.profile_preview.logo_url"
                    :alt="step.profile_preview.name"
                >
                <span v-else class="todo-step-card__profile-logo-missing">{{ labels.logoMissing }}</span>
            </div>
            <div class="todo-step-card__profile-details">
                <p class="todo-step-card__company-name">{{ step.profile_preview.name }}</p>
                <p class="todo-step-card__flyer">
                    <span class="todo-step-card__flyer-label">{{ labels.flyerLabel }}:</span>
                    <a
                        v-if="step.profile_preview.advert_url"
                        :href="step.profile_preview.advert_url"
                        target="_blank"
                        rel="noopener"
                    >{{ labels.flyerView }}</a>
                    <span v-else class="todo-step-card__flyer-empty">{{ labels.flyerEmpty }}</span>
                </p>
            </div>
        </div>
        <p v-else class="todo-step-card__summary">{{ step.summary }}</p>

        <ul v-if="step.files && step.files.length" class="todo-step-card__files">
            <li v-for="file in step.files" :key="file.key" class="todo-step-card__file">
                <span class="todo-step-card__file-label">{{ file.label }}</span>
                <span
                    class="todo-step-card__file-status"
                    :class="file.uploaded ? 'is-provided' : 'is-missing'"
                >
                    {{ file.uploaded ? labels.fileProvided : labels.fileMissing }}
                </span>
                <a v-if="file.uploaded && file.url" :href="file.url" target="_blank" class="btn--underline">
                    {{ labels.openFile }}
                </a>
            </li>
        </ul>

        <p v-if="step.modification_closed && !step.acknowledged" class="todo-step-card__closed-notice">
            {{ labels.modificationClosed }}
        </p>

        <div class="todo-step-card__actions">
            <a
                v-if="step.actions.indexOf('modify') !== -1"
                :href="modifyUrlWithOrigin"
                class="btn-cta"
            >
                {{ step.actions.indexOf('acknowledge') !== -1 ? labels.modify : labels.complete }}
            </a>
            <a
                v-if="step.actions.indexOf('view') !== -1"
                :href="step.view_route"
                class="btn-cta"
            >
                {{ labels.view }}
            </a>
            <div v-if="step.current_external_link" class="todo-step-card__external-link">
                <input type="text" :value="step.current_external_link" readonly ref="externalLinkInput">
                <button type="button" class="btn--underline" @click="copyExternalLink">
                    {{ copyButtonLabel }}
                </button>
            </div>
            <form
                v-else-if="step.external_link_route"
                :action="externalLinkUrlWithOrigin"
                method="POST"
                class="todo-step-card__inline-form"
            >
                <input type="hidden" name="_token" :value="csrfToken">
                <button type="submit" class="btn--underline">
                    {{ labels.generateExternalLink }}
                </button>
            </form>
            <button
                v-if="!step.acknowledged && step.actions.indexOf('acknowledge') !== -1"
                @click="acknowledge"
                type="button"
                class="btn--underline"
                :disabled="isSubmitting || step.can_acknowledge === false"
            >
                {{ labels.confirm }}
            </button>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'TodoStepCard',
    props: {
        step: { type: Object, required: true },
        companyId: { type: Number, required: true },
        isFocused: { type: Boolean, default: false },
        // Translated UI strings — passed from the Blade wrapper.
        labels: {
            type: Object,
            required: true,
            // expected keys: modify, complete, confirm, modificationClosed
        },
    },
    data() {
        return {
            isSubmitting: false,
            externalLinkJustCopied: false,
        };
    },
    computed: {
        modifyUrlWithOrigin() {
            // Append the ?from=todo-list&step=<key> origin marker so the
            // server middleware can route the user back to the modal on save.
            const separator = this.step.modify_route.indexOf('?') === -1 ? '?' : '&';
            return `${this.step.modify_route}${separator}from=todo-list&step=${this.step.key}`;
        },
        externalLinkUrlWithOrigin() {
            // Same origin marker on the generate-link POST so the user lands on
            // the form with a session-primed `from_todo_list` and the back link
            // points back to the modal.
            if (!this.step.external_link_route) {
                return '';
            }
            const separator = this.step.external_link_route.indexOf('?') === -1 ? '?' : '&';
            return `${this.step.external_link_route}${separator}from=todo-list&step=${this.step.key}`;
        },
        csrfToken() {
            const meta = document.querySelector('meta[name="csrf-token"]');
            return meta ? meta.getAttribute('content') : '';
        },
        copyButtonLabel() {
            return this.externalLinkJustCopied
                ? (this.labels.externalLinkCopied || '✓')
                : (this.labels.copyExternalLink || 'Copy');
        },
    },
    methods: {
        copyExternalLink() {
            const value = this.step.current_external_link;
            if (!value) return;
            const writer = navigator.clipboard && navigator.clipboard.writeText
                ? navigator.clipboard.writeText(value)
                : new Promise((resolve) => {
                    const input = this.$refs.externalLinkInput;
                    if (input) { input.select(); document.execCommand('copy'); }
                    resolve();
                });
            writer.then(() => {
                this.externalLinkJustCopied = true;
                setTimeout(() => { this.externalLinkJustCopied = false; }, 2000);
            });
        },
        async acknowledge() {
            this.isSubmitting = true;
            try {
                const url = `/company/${this.companyId}/todo-list/acknowledge/${this.step.key}`;
                const { data } = await axios.post(url);
                this.$emit('acknowledged', { key: this.step.key, isComplete: data.is_complete });
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>
